import CandlestickSettings from './candlestickSettings'
import LineSettingsDialogs from './lineSettingsDialogs'
import React, { memo, useContext, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ChartType } from '../../proto/generated/chart'
import AreaSettingsDialogs from './areaSettingsDialogs'
import BarSettingsDialogs from './barSettingsDialogs'
import HeikinSettingsDialogs from './heikinSettingsDialogs'
import HistoricalSettingsDialogs from './histogramSettingsDialogs'
import { updateChartConfig } from '../../reducer/chart.reducer'
import { selectChartConfigSettings, selectChartConfig } from '../../selectors/chart'
import { SeriesOptions } from '../chartV2/chart.typings'
import { AppContext } from '../../App'
import { Button, DialogActions, DialogContent, Divider, Typography, Stack } from '@mui/material'
import DraggableDialogTitle from '../common/DraggableDialogTitle'
import { Dialog } from '@mui/material'
import { ExtraSmallButton } from "../custom";
import ClearIcon from "../../assets/icons/ClearIcon";
import HollowCandlestickSettings from './hollowCandlestickSettings'
import HighLowCandlestickSettings from './highLowCandlestickSettings'
import BaselineSettings from './baselineSettings'
import HlcAreaSettings from './hlcAreaSettings'
import RenkoSettings from './renkoSettings'
import KagiSettings from './kagiSettings'
import PandFSettings from './pandFSettings'
import LinkBreakSettings from './lineBreakSettings'
import VolumeCandlesSettings from './volumeCandleSettings'
import StepLineSettings from './stepLineSettings'
import { defaultTradingSettings } from '../../util/chart'

const DEFAULT_STATUS_LINE_SETTINGS = {
    logo: false,
    showSymbolName: true,
    showOHLC: true,
    showBarChange: true,
    showLastDayChange: false,
    showExchange: true,
    indicatorTitle: true,
    indicatorInputs: false,
    indicatorValues: true
} as const

type StatusLineSettings = typeof DEFAULT_STATUS_LINE_SETTINGS

const areStatusLineSettingsEqual = (a: StatusLineSettings, b: StatusLineSettings) => (
    a.logo === b.logo &&
    a.showSymbolName === b.showSymbolName &&
    a.showOHLC === b.showOHLC &&
    a.showBarChange === b.showBarChange &&
    a.showLastDayChange === b.showLastDayChange &&
    a.showExchange === b.showExchange &&
    a.indicatorTitle === b.indicatorTitle &&
    a.indicatorInputs === b.indicatorInputs &&
    a.indicatorValues === b.indicatorValues
)

interface Props {
    open: boolean;
    onClose: () => void;
    viewKey: string;
    chartType: ChartType;
}

export const DEFAULT_SETTINGS = {
    [ChartType.CANDLESTICK]: {
        visible: true,
        upColor: '#26a69a',
        downColor: '#ef5350',
        borderVisible: false,
        borderUpColor: '#26a69a',
        borderDownColor: '#ef5350',
        wickVisible: true,
        wickUpColor: '#26a69a',
        wickDownColor: '#ef5350'
    },
    [ChartType.HLC_AREA]: {
        highLineColor: '#26a69a',
        lowLineColor: '#ef5350',
        closeLineColor: '#26a69a',
        topFillColor: '#ef5350',
        bottomFillColor: '#26a69a',
        highLineVisible: true,
        lowLineVisible: false
    },

    [ChartType.STEP_LINE_CHART]: {
        color: '#2962FF'
    },
    [ChartType.HIGH_LOW]: {
        visible: true,
        upColor: '#26a69a',
        downColor: '#26a69a',
        borderVisible: true,
        borderUpColor: '#26a69a',
        borderDownColor: '#26a69a',
        wickVisible: false,
        wickUpColor: '#26a69a',
        wickDownColor: '#26a69a'
    },
    [ChartType.LINE]: {
        color: '#2196f3',
        source: 'close'
    },
    [ChartType.LINE_WITH_MARKERS]: {
        color: '#2196f3',
        source: 'close'
    },
    [ChartType.AREA]: {
        topColor: 'rgba( 46, 220, 135, 0.4)',
        bottomColor: 'rgba( 40, 221, 100, 0)',
        lineColor: '#33D778',
        source: 'close'
    },
    [ChartType.BAR]: {
        upColor: '#26a69a',
        downColor: '#ef5350',
        thinBars: false
    },
    [ChartType.LINE_BREAK]: {
        upColor: '#26a69a',
        downColor: '#ef5350',
        borderVisible: true,
        borderUpColor: '#26a69a',
        borderDownColor: '#ef5350',
    },
    [ChartType.VOLUME_CANDLESTICK]: {
        upColor: '#26a69a',
        downColor: '#ef5350',
        borderVisible: true,
        borderUpColor: '#26a69a',
        borderDownColor: '#ef5350',
        wickUpColor: '#26a69a',
        wickDownColor: '#ef5350',
    },
    [ChartType.HEIKIN_ASHI]: {
        visible: true,
        upColor: '#26a69a',
        downColor: '#ef5350',
        borderVisible: false,
        borderUpColor: '#26a69a',
        borderDownColor: '#ef5350',
        wickVisible: true,
        wickUpColor: '#26a69a',
        wickDownColor: '#ef5350'
    },
    [ChartType.HOLLOW_CANDLES]: {
        visible: true,
        upColorHollow: '#26a69a',
        downColorHollow: '#ef5350',
        borderVisibleHollow: false,
        borderUpColorHollow: '#26a69a',
        borderDownColorHollow: '#ef5350',
        wickVisibleHollow: true,
        wickUpColorHollow: '#26a69a',
        wickDownColorHollow: '#ef5350'
    },
    [ChartType.HISTOGRAM]: {
        upColor: '#26a69a',
        downColor: '#ef5350',
    },
    [ChartType.BASELINE]: {
        source: "close",
        baseValue: 50,
        topLineColor: '#089981',
        bottomLineColor: '#f23645',
        topFillColor1: '#08998100',
        topFillColor2: '#08998147',
        bottomFillColor1: '#f2364500',
        bottomFillColor2: '#f2364547',
    },
    [ChartType.KAGI]: {
        upColor: '#26a69a',
        downColor: '#ef5350',
        projectionUpColor: '#a9dcc3',
        projectionDownColor: '#f5a6ae',
        boxMethod: 'traditional',
        atrLength: 14,
        percentageLTP: 1,
        reversalAmount: 2
    },
    [ChartType.POINT_AND_FIGURE]: {
        upColor: '#26a69a',
        downColor: '#ef5350',
        boxMethod: 'atr',
        boxSize: 1,
        atrLength: 14,
        percentage: 1,
        reversalAmount: 3
    },
    [ChartType.RENKO]: {
        upColor: '#26a69a',
        downColor: '#ef5350',
        borderVisible: true,
        borderUpColor: '#26a69a',
        borderDownColor: '#ef5350',
        wickVisible: true,
        wickUpColor: '#26a69a',
        wickDownColor: '#ef5350',
        boxMethod: 'atr',
        boxSize: 10,
        atr: 14,
        percentage: 1,
        projectionUpColor: '#a9dcc3',
        projectionDownColor: '#f5a6ae',
        projectionUpBorderColor: '#a9dcc3',
        projectionDownBorderColor: '#f5a6ae'
    }
} as const
const chartSettingsComponents = (viewKey: string, chartType: ChartType) => {
    switch (chartType) {
        case ChartType.CANDLESTICK:
            return <CandlestickSettings viewKey={viewKey} />
        case ChartType.LINE:
            return <LineSettingsDialogs viewKey={viewKey} />
        case ChartType.AREA:
            return <AreaSettingsDialogs viewKey={viewKey} />
        case ChartType.BAR:
            return <BarSettingsDialogs viewKey={viewKey} />
        case ChartType.HEIKIN_ASHI:
            return <HeikinSettingsDialogs viewKey={viewKey} />
        case ChartType.HISTOGRAM:
            return <HistoricalSettingsDialogs viewKey={viewKey} />
        case ChartType.HOLLOW_CANDLES:
            return <HollowCandlestickSettings viewKey={viewKey} />
        case ChartType.HIGH_LOW:
            return <HighLowCandlestickSettings viewKey={viewKey} />
        case ChartType.BASELINE:
            return <BaselineSettings viewKey={viewKey} />
        case ChartType.HLC_AREA:
            return <HlcAreaSettings viewKey={viewKey} />
        case ChartType.RENKO:
            return <RenkoSettings viewKey={viewKey} />
        case ChartType.KAGI:
            return <KagiSettings viewKey={viewKey} />
        case ChartType.POINT_AND_FIGURE:
            return <PandFSettings viewKey={viewKey} />
        case ChartType.LINE_BREAK:
            return <LinkBreakSettings viewKey={viewKey} />
        case ChartType.VOLUME_CANDLESTICK:
            return <VolumeCandlesSettings viewKey={viewKey} />
        case ChartType.STEP_LINE_CHART:
            return <StepLineSettings viewKey={viewKey} />
        case ChartType.LINE_WITH_MARKERS:
            return <LineSettingsDialogs viewKey={viewKey} />
        default:
            return <></>
    }
}

const ChartSettingsDialogs: React.FC<Props> = ({ open, onClose, viewKey, chartType }) => {
    const dispatch = useDispatch()
    const appContext = useContext(AppContext)
    const chart = appContext.getChart(viewKey)
    const getDefaultSettings = (chartType: ChartType) => {
        return DEFAULT_SETTINGS[chartType as keyof typeof DEFAULT_SETTINGS] || {}
    }
    const prevSettingsRef = useRef<SeriesOptions>({})
    const statusLineSettings = (useSelector(selectChartConfigSettings(viewKey)) ?? DEFAULT_STATUS_LINE_SETTINGS) as StatusLineSettings
    const chartConfig = useSelector(selectChartConfig(viewKey))
    const tradingSettings = chartConfig?.tradingSettings || defaultTradingSettings
    const [currentStatusLineSettings, setCurrentStatusLineSettings] = useState<StatusLineSettings>(DEFAULT_STATUS_LINE_SETTINGS)
    const [prevStatusLineSettings, setPrevStatusLineSettings] = useState<StatusLineSettings>(DEFAULT_STATUS_LINE_SETTINGS)
    const [currentTradingSettings, setCurrentTradingSettings] = useState(defaultTradingSettings)
    const [prevTradingSettings, setPrevTradingSettings] = useState(defaultTradingSettings)

    useEffect(() => {
        if (open && chart) {
            const settings = chart.primarySeriesGroup?.getPrimarySeries()?.getDefaultOptions()
            prevSettingsRef.current = { ...(settings ?? getDefaultSettings(chartType)) }
        }
    }, [open])

    useEffect(() => {
        if (open && chart) {
            const appliedStatusLineSettings = statusLineSettings
            const appliedTradingSettings = tradingSettings
            setCurrentStatusLineSettings((prev) =>
                areStatusLineSettingsEqual(prev, appliedStatusLineSettings) ? prev : (appliedStatusLineSettings as any)
            )
            setPrevStatusLineSettings((prev) =>
                areStatusLineSettingsEqual(prev, appliedStatusLineSettings) ? prev : (appliedStatusLineSettings as any)
            )
            setCurrentTradingSettings((prev) => (prev === appliedTradingSettings ? prev : (appliedTradingSettings as any)))
            setPrevTradingSettings((prev) => (prev === appliedTradingSettings ? prev : (appliedTradingSettings as any)))
        }
    }, [open, chartType, statusLineSettings, tradingSettings, chart])

    const handleApplyDefault = () => {
        const defaultSettings = getDefaultSettings(chartType)
        const defaultStatusLineSettings = {
            logo: true,
            showSymbolName: true,
            showOHLC: true,
            showBarChange: true,
            showLastDayChange: false,
            showExchange: true,
            indicatorTitle: true,
            indicatorInputs: false,
            indicatorValues: true
        } as any
        if (chart) {
            chart.chartConfig = {
                ...chart.chartConfig,
                statusLineSettings: defaultStatusLineSettings,
                tradingSettings: defaultTradingSettings
            }
            const primarySeries = chart.primarySeriesGroup?.getPrimarySeries()
            if (primarySeries) {
                primarySeries.updateOptions(defaultSettings as SeriesOptions)
            } else {
                dispatch(updateChartConfig({
                    viewKey,
                    config: {
                        statusLineSettings: defaultStatusLineSettings,
                        tradingSettings: defaultTradingSettings
                    },
                    createIfAbsent: false
                }))
            }
        }
        setCurrentStatusLineSettings(defaultStatusLineSettings)
        setPrevStatusLineSettings(defaultStatusLineSettings)
        setCurrentTradingSettings(defaultTradingSettings)
        setPrevTradingSettings(defaultTradingSettings)
    }

    const handleCancel = () => {
        if (!chart) return
        dispatch(updateChartConfig({
            viewKey,
            config: {
                statusLineSettings: prevStatusLineSettings,
                tradingSettings: prevTradingSettings
            },
            createIfAbsent: false
        }))
        if (chart) {
            const primarySeries = chart.primarySeriesGroup?.getPrimarySeries()
            if (primarySeries) {
                primarySeries.updateOptions(prevSettingsRef.current as SeriesOptions)
            }
        }
        setCurrentStatusLineSettings(prevStatusLineSettings)
        setCurrentTradingSettings(prevTradingSettings)
        onClose()
    }

    const handleOk = () => {
        setPrevStatusLineSettings(currentStatusLineSettings)
        setPrevTradingSettings(currentTradingSettings)
        onClose()
    }
    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{
            sx: {
                minHeight: "40%", maxHeight: 'max-content'
            }
        }}>
            <DraggableDialogTitle>
                <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                    <Typography variant={'h6'}>Settings</Typography>
                    <ExtraSmallButton onClick={onClose} aria-label="close" color={"inherit"}>
                        <ClearIcon />
                    </ExtraSmallButton>
                </Stack>
            </DraggableDialogTitle>
            <DialogContent sx={{ padding: "0px", display: "flex", flexDirection: "column", height: '100%' }}>
                {chartSettingsComponents(viewKey, chartType) || <div>No settings available</div>}
            </DialogContent>
            <Divider />
            <DialogActions>
                <Button variant="outlined" onClick={handleApplyDefault} sx={{ marginRight: 'auto' }}>
                    Apply Default
                </Button>
                <Button variant="outlined" onClick={handleCancel}>
                    Cancel
                </Button>
                <Button onClick={handleOk} variant="contained">
                    Ok
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default memo(ChartSettingsDialogs)